import { CAPABILITIES, type ActorSource } from '@lemontaigu/platform';
import { requireCapability } from './capabilityGate.js';
import type { BoardDayUpsert, BoardWriteStore } from './ports.js';
import type { ActivityPresence, BoardAbsence } from './activityBoard.js';

/**
 * DRIVING port for the board's presence/absence use cases (ladder step 7).
 *
 * WHAT A DRIVING PORT IS FOR HERE. The four call sites this replaces did not
 * differ in what they meant — « I am working on this activity », « mark this
 * person off » — they differed in the incidental details of how each author
 * spelled it: which table went first, which `source` value, whether the second
 * write's error was checked (it never was). Naming the four use cases makes
 * those the same thing in one place, and gives step 9 a single spot to put
 * `assertCapability`.
 *
 * WHO IS ACTING is a parameter, not an ambient fact. The same use case is
 * reachable by an employee tapping their own row (`source: 'tap'`), the desk
 * declaring on someone's behalf (`'desk'`), and the operator pinning an
 * assignment (`'operator'`). RLS decides which of those the caller may actually
 * perform; this interface only refuses to pretend they are different use cases.
 */
export interface BoardPresenceService {
  /**
   * Record that someone is on an activity that day. Clears any absence for the
   * day in the same transaction — the invariant, not a courtesy.
   */
  declarePresence(input: {
    activityId: number;
    employeeId: string;
    date: string;
    status: 'declared' | 'assigned';
    source: ActivityPresence['source'];
    /** The acting EMPLOYEE when known: self on a tap, the « via » receptionist on a desk write. */
    declaredBy: string | null;
  }): Promise<void>;

  /** Withdraw one activity declaration/assignment. Absence is not touched. */
  withdrawPresence(activityId: number, employeeId: string, date: string): Promise<void>;

  /**
   * Mark a day off. Clears that day's presence rows in the same transaction and
   * FAILS if any of them could not be cleared, rather than leaving the employee
   * both present and absent (migration 0055).
   */
  markAbsent(input: {
    employeeId: string;
    date: string;
    source: BoardAbsence['source'];
    declaredBy: string | null;
  }): Promise<void>;

  /** Lift a day-off marker. Presence is not restored — it was deleted, not hidden. */
  clearAbsence(employeeId: string, date: string): Promise<void>;
}

/**
 * The only implementation. It is thin, and that is the design rather than a
 * placeholder: the invariant belongs to the transaction (ARCHITECTURE.md §2 —
 * the TypeScript domain is a functional core, never an invariant enforcer), so
 * a service that "helpfully" re-checked it here would be claiming a guarantee
 * it cannot make against a concurrent writer.
 *
 * What the seam buys is not logic today but placement, and what lets a view be
 * tested over `FakeBoardWriteStore` without a database.
 *
 * ⚠ NO L2 CAPABILITY GATE HERE, AND THAT IS THE DECISION (2026-07-28, F3 L2
 * increment 3 — ladder §3.19). Earlier revisions of this comment said this was
 * "where step 9's `assertCapability` goes". It is not, and the reason is written
 * three paragraphs up: WHO IS ACTING is a parameter. Presence writes are
 * authorized by FOUR separate RLS policies OR'd together — an employee tapping
 * their own row passes `bap_self_tap_insert` on IDENTITY alone
 * (`employee_id = staff_employee_id()`), holding no capability whatsoever, while
 * the desk passes on `rh.board.write` and the operator on `rh.board.assign`.
 *
 * A `requireCapability` here would therefore be STRICTER than the database and
 * would break the employee self-service tap in `apps/staff` — the one thing this
 * use case exists for. The gate belongs where the use case is genuinely
 * operator-only: `BoardDemandService` below, and the four services in
 * `adminServices.ts`. Check E12 now enforces this.
 */
export function createBoardPresenceService(store: BoardWriteStore): BoardPresenceService {
  return {
    declarePresence: (input) => store.setPresence(input),
    withdrawPresence: (activityId, employeeId, date) => store.clearPresence(activityId, employeeId, date),
    markAbsent: (input) => store.setAbsence(input),
    clearAbsence: (employeeId, date) => store.clearAbsence(employeeId, date),
  };
}

/**
 * DRIVING port for the demand grid and the per-day activity overrides
 * (ladder step 7). Separate from `BoardPresenceService` because it is a
 * different family of use case with a different actor: presence is « who is
 * working », demand is « how much work there is », and only the desk and the
 * operator ever touch the second.
 */
export interface BoardDemandService {
  /**
   * Save the demand figures for one day (the Jour gesture) or a whole week (the
   * Semaine grid) — one call either way, so a partially-saved week is not a
   * state the caller has to reason about.
   */
  saveDemand(days: BoardDayUpsert[]): Promise<void>;

  /**
   * « Recalculer la chaîne d'occupation » — rewrite occupancy + recouches for a
   * run of days, touching nothing else the desk typed.
   */
  saveOccupancyChain(rows: { date: string; occupancy: number; stayovers: number }[]): Promise<void>;

  /** The desk's only remaining board write (M4): dîners + note. Partial by
   *  construction — the RPC behind it cannot reach a feed-owned column. */
  saveDeskDay(row: { date: string; diners: number; note: string | null }): Promise<void>;

  /** Override an activity's window/requirement for one day. */
  saveOverride(input: {
    activityId: number;
    date: string;
    windowStart: string | null;
    windowEnd: string | null;
    required: boolean;
    note: string | null;
  }): Promise<void>;

  /** Drop an override, restoring the activity's default window for that day. */
  clearOverride(overrideId: number): Promise<void>;
}

export function createBoardDemandService(
  store: BoardWriteStore,
  actor: ActorSource,
): BoardDemandService {
  // Gated, unlike presence above, because the tables differ: `board_days` and
  // `board_activity_overrides` are « how much work there is » — nobody's own row,
  // no identity policy on either, so the capability really is the whole rule.
  const gate = () => requireCapability(actor, CAPABILITIES.RH_BOARD_WRITE);
  return {
    saveDemand: async (days) => {
      gate();
      return store.saveDays(days);
    },
    saveOccupancyChain: async (rows) => {
      gate();
      return store.saveOccupancyChain(rows);
    },
    saveDeskDay: async (row) => {
      gate();
      return store.saveDeskDay(row);
    },
    saveOverride: async (input) => {
      gate();
      return store.saveOverride(input);
    },
    clearOverride: async (overrideId) => {
      gate();
      return store.clearOverride(overrideId);
    },
  };
}
